/**
 * <scroll-rail>  Horizontal scroll-snap row (CSS lives in base.css, `.rail`).
 *                Arrow buttons can sit anywhere in the page:
 *                <button data-rail-prev="RAIL_ID"> / <button data-rail-next="RAIL_ID">
 *
 * <tab-group>    Tabs (role="tab" + aria-controls) that show one panel at a time.
 *                When the panels hold rails, the group's arrows follow the active one.
 */
(function () {
  if (customElements.get('scroll-rail')) return;

  const ARROWS = '[data-rail-prev], [data-rail-next]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.refresh = this.refresh.bind(this);
      this.addEventListener('scroll', this.refresh, { passive: true });
      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(this);
      this.update();
      // Pills rails: bring the selected pill (aria-current) into the middle instead of starting at the left.
      if (this.hasAttribute('data-center-current')) {
        // Until the visitor scrolls it themselves, keep re-centering as fonts load and widths settle.
        this.stopCentering = () => { this.userScrolled = true; };
        ['pointerdown', 'wheel', 'touchstart'].forEach((type) =>
          this.addEventListener(type, this.stopCentering, { passive: true, once: true })
        );
        this.centerCurrent();
        document.fonts.ready.then(() => this.centerCurrent());
        window.addEventListener('load', () => this.centerCurrent(), { once: true });
      }
    }

    centerCurrent() {
      if (this.userScrolled) return;
      const current = this.querySelector('[aria-current]');
      if (!current) return;
      const left = current.offsetLeft + current.offsetWidth / 2 - this.clientWidth / 2;
      this.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
      this.centeredWidth = this.scrollWidth;
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.refresh);
      this.resizeObserver.disconnect();
      cancelAnimationFrame(this.frame);
    }

    get arrows() {
      if (!this.id) return [];
      return Array.from(
        document.querySelectorAll(`[data-rail-prev="${this.id}"], [data-rail-next="${this.id}"]`)
      );
    }

    // Scroll and resize fire in bursts: update at most once per frame.
    refresh() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    get progressBars() {
      if (!this.id) return [];
      return Array.from(document.querySelectorAll(`[data-rail-progress="${this.id}"]`));
    }

    // Disable the arrows at either end, hide them when every item already fits,
    // and move the progress bar's thumb along with the scroll position.
    update() {
      // Content width changed (font swap, late images) before any user scroll: re-center the current pill.
      if (this.hasAttribute('data-center-current') && !this.userScrolled && this.centeredWidth !== this.scrollWidth) {
        this.centerCurrent();
      }
      const arrows = this.arrows;
      const max = this.scrollWidth - this.clientWidth;
      const position = Math.abs(this.scrollLeft);
      const overflows = max > 1;

      this.progressBars.forEach((bar) => {
        bar.hidden = !overflows;
        const size = overflows ? Math.max(8, (this.clientWidth / this.scrollWidth) * 100) : 100;
        const offset = overflows ? (position / max) * (100 - size) : 0;
        bar.style.setProperty('--thumb-size', `${size}%`);
        bar.style.setProperty('--thumb-offset', `${offset}%`);
        bar.setAttribute('aria-valuenow', String(Math.round(overflows ? (position / max) * 100 : 100)));
      });

      if (!arrows.length) return;

      arrows.forEach((arrow) => {
        arrow.disabled = arrow.hasAttribute('data-rail-prev') ? position <= 1 : position >= max - 1;

        // Hide the arrows' wrapper when it holds nothing else, otherwise just the button.
        const wrapper = arrow.parentElement;
        const onlyArrows = wrapper && Array.from(wrapper.children).every((child) => child.matches(ARROWS));
        (onlyArrows ? wrapper : arrow).hidden = !overflows;
      });
    }

    // Scroll one "page" (the visible items) back (-1) or forward (1).
    page(direction) {
      const style = getComputedStyle(this);
      const gap = parseFloat(style.columnGap) || 0;
      const rtl = style.direction === 'rtl' ? -1 : 1;

      this.scrollBy({
        left: direction * rtl * (this.clientWidth + gap),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
  }

  /* Dragging the progress bar scrolls the rail: grab the thumb, or press the track to jump
     the thumb under the finger and keep dragging from there. */
  const drag = { bar: null, rail: null, grab: 0, pointerId: null };

  const thumbMetrics = (bar, rail) => {
    const rect = bar.getBoundingClientRect();
    const max = rail.scrollWidth - rail.clientWidth;
    const size = Math.max(0.08, rail.clientWidth / rail.scrollWidth);
    const thumbWidth = rect.width * size;
    const track = rect.width - thumbWidth;
    const thumbLeft = max > 0 ? (Math.abs(rail.scrollLeft) / max) * track : 0;
    return { rect, max, thumbWidth, track, thumbLeft };
  };

  const scrollTo = (x) => {
    const { bar, rail, grab } = drag;
    const { rect, max, track } = thumbMetrics(bar, rail);
    if (track <= 0) return;
    const ratio = Math.min(1, Math.max(0, (x - rect.left - grab) / track));
    rail.scrollLeft = ratio * max * (getComputedStyle(rail).direction === 'rtl' ? -1 : 1);
  };

  const endDrag = () => {
    const { bar, rail } = drag;
    if (!bar) return;
    bar.classList.remove('is-dragging');
    rail.classList.remove('is-dragging');
    rail.style.scrollSnapType = '';
    rail.style.scrollBehavior = '';
    drag.bar = drag.rail = null;
  };

  document.addEventListener('pointerdown', (event) => {
    const bar = event.target.closest('[data-rail-progress]');
    if (!bar || event.button !== 0) return;
    const rail = document.getElementById(bar.dataset.railProgress);
    if (!(rail instanceof ScrollRail)) return;

    event.preventDefault();
    const { thumbWidth, thumbLeft, rect } = thumbMetrics(bar, rail);
    const x = event.clientX - rect.left;
    const onThumb = x >= thumbLeft && x <= thumbLeft + thumbWidth;

    drag.bar = bar;
    drag.rail = rail;
    drag.grab = onThumb ? x - thumbLeft : thumbWidth / 2;
    drag.pointerId = event.pointerId;
    bar.classList.add('is-dragging');
    rail.classList.add('is-dragging');
    rail.style.scrollSnapType = 'none';
    rail.style.scrollBehavior = 'auto';
    try { bar.setPointerCapture(event.pointerId); } catch (e) { /* older browsers */ }
    scrollTo(event.clientX);
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag.bar || event.pointerId !== drag.pointerId) return;
    event.preventDefault();
    scrollTo(event.clientX);
  });

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
      // Theme editor: show the tab whose block was selected in the sidebar.
      this.addEventListener('shopify:block:select', this);
    }

    get tabs() {
      return Array.from(this.querySelectorAll('[role="tab"]'));
    }

    handleEvent(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !this.contains(tab)) return;

      if (event.type !== 'keydown') {
        this.select(tab);
        return;
      }

      const tabs = this.tabs;
      let index = tabs.indexOf(tab);

      switch (event.key) {
        case 'ArrowRight':
          index = (index + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          index = (index - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          index = 0;
          break;
        case 'End':
          index = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      tabs[index].focus();
      this.select(tabs[index]);
    }

    select(tab) {
      let activePanel = null;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        const panel = document.getElementById(item.getAttribute('aria-controls'));

        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        if (panel) panel.hidden = !selected;
        if (panel && selected) activePanel = panel;
      });

      if (!activePanel) return;

      // Point the header arrows at the rail that is now visible.
      const rail = activePanel.querySelector('scroll-rail');
      this.querySelectorAll(ARROWS).forEach((arrow) => {
        const name = arrow.hasAttribute('data-rail-prev') ? 'data-rail-prev' : 'data-rail-next';
        arrow.setAttribute(name, rail ? rail.id : '');
      });
      if (rail) rail.update();

      // Keep the header "view all" link on the active collection.
      const link = this.querySelector('.section-header .link-u');
      if (link && activePanel.dataset.url) link.href = activePanel.dataset.url;
    }
  }

  customElements.define('scroll-rail', ScrollRail);
  customElements.define('tab-group', TabGroup);

  // One delegated listener, so arrows keep working after a section is re-rendered.
  document.addEventListener('click', (event) => {
    const arrow = event.target.closest(ARROWS);
    if (!arrow) return;

    const rail = document.getElementById(arrow.dataset.railPrev || arrow.dataset.railNext);
    if (rail instanceof ScrollRail) rail.page(arrow.hasAttribute('data-rail-prev') ? -1 : 1);
  });
})();
